import { useMemo, useState } from "react";
import type { FulfillmentOrderLine } from "../../types";
import { changedEntryFields, type EntryExistingDraft, type EntryParseResult, type EntryPreviewItem } from "../../service/confirmationEditorEntry";
import { calculateFulfillmentLineAmount, confirmationTravelerRoleLabel, sumFulfillmentAmounts } from "../../service/fulfillmentOrderEditorService";
import { formatConfirmationAmount as amountText, formatConfirmationDecimal as compact } from "../../service/confirmationDecimalDisplay";
import { ConfirmationButton } from "./primitives";
import type { ConfirmationEntryProps } from "./types";
export type ConfirmationNewPreview = EntryPreviewItem & {
    previewKey: string;
};
export { confirmationTravelerRoleLabel } from "../../service/fulfillmentOrderEditorService";
/** 新旧费用共用分类列，身份与修改方式独立保留。 */
export function ticketEntryColumns(): string[] {
    return ["选择", "SKU / 计费方式", "计算依据", "金额", "服务主体", "结算主体", "结果"];
}
/** 连续分类预览的主体分组，已有行以原 ID 回写，新行保留原文定位。 */
export function ConfirmationTicketEntryRows({ rows, newRows, issues, groupId, groupLabel, rangeLabel, active, onFocusGroup, disabled, selectedNew, onSelectNew, selectedExisting, onSelectExisting, onLocate, hideGroupHeading, locations = {} }: {
    rows: EntryExistingDraft[];
    newRows: ConfirmationNewPreview[];
    issues: EntryParseResult["errors"];
    groupId: string;
    groupLabel: string;
    rangeLabel: string;
    active: boolean;
    onFocusGroup: () => void;
    onChange: (rows: EntryExistingDraft[]) => void;
    runtime: ConfirmationEntryProps["runtime"];
    disabled: boolean;
    selectedNew: ReadonlySet<string>;
    onSelectNew: (key: string, selected: boolean) => void;
    selectedExisting?: ReadonlySet<string>;
    onSelectExisting?: (ids: string[], selected: boolean) => void;
    mealDays?: ReadonlyArray<{
        dayNo: number;
        date: string | null;
    }>;
    onMoveMeal?: (itemKey: string, targetDay: number) => void;
    onDragMeal?: (itemKey: string | null) => void;
    onLocate: (groupId: string, start: number, end: number, issue?: EntryParseResult["errors"][number]) => void;
    hideGroupHeading?: boolean;
    locations?: Record<string, { sourceStart: number; sourceEnd: number }>;
}) {
    const [localSelected, setLocalSelected] = useState<Set<string>>(new Set());
    const sourceSelected = selectedExisting ?? localSelected;
    const selected = useMemo(() => new Set(rows.filter((row) => !row.removed && sourceSelected.has(row.base.id)).map((row) => row.base.id)), [rows, sourceSelected]);
    const selectExisting = (ids: string[], checked: boolean) => {
        if (disabled)
            return;
        if (onSelectExisting) {
            onSelectExisting(ids, checked);
            return;
        }
        setLocalSelected((previous) => {
            const next = new Set(previous);
            for (const id of ids) {
                if (checked)
                    next.add(id);
                else
                    next.delete(id);
            }
            return next;
        });
    };
    const columnCount = ticketEntryColumns().length;
    const records: Array<{
        row?: EntryExistingDraft;
        addition?: ConfirmationNewPreview;
        line: Partial<FulfillmentOrderLine>;
        amount: string;
    }> = rows.map((row) => {
        const changed = changedEntryFields(row);
        let amount = row.base.amount;
        if (["quantity", "billingCount", "unitPriceAmount"].some((name) => name in changed)) {
            const normalized = { ...row.base, ...changed };
            try {
                amount = calculateFulfillmentLineAmount(normalized.quantity, normalized.billingCount, normalized.unitPriceAmount);
            }
            catch {
                amount = "";
            }
        }
        return { row, line: { ...row.base, ...row.patch }, amount };
    });
    records.push(...newRows.map((addition) => ({ addition, line: addition.patch, amount: addition.patch.amount || "0" })));
    const subtotal = sumFulfillmentAmounts(records.filter((item) => !item.row?.removed).map((item) => item.amount || "0"));
    return <tbody className="confirmation-entry-group" data-entry-group={groupId} data-active={active || undefined}>
    <tr className="confirmation-entry-group-heading" data-compact={hideGroupHeading || undefined}><th colSpan={columnCount}><div>{!hideGroupHeading ? <><button type="button" onClick={onFocusGroup}>{groupLabel}</button><span>{rangeLabel}</span><strong>{amountText(subtotal)}</strong></> : <span>勾选费用可设置主体</span>}
      {rows.some((row) => !row.removed) ? <ConfirmationButton size="sm" variant="ghost" disabled={disabled} onClick={() => selectExisting(rows.filter((row) => !row.removed).map((row) => row.base.id), selected.size !== rows.filter((row) => !row.removed).length)}>全选本组</ConfirmationButton> : null}</div></th></tr>
    {selected.size ? <tr><td colSpan={columnCount}><div className="confirmation-entry-bulk"><span>已选 {selected.size} 行</span>
    </div></td></tr> : null}
    {records.map(({ row, line, amount, addition }) => {
            const subject = (kind: "fulfillment" | "settlement") => <span className={!line[`${kind}SupplierName`] ? "confirmation-entry-unset" : ""}>{line[`${kind}SupplierName`] || "待设置"}</span>;
            const showServiceSubject = false;
            const itemTitle = line.skuNameSnapshot || line.itemSpecSnapshot || line.itemNameSnapshot;
            const source = addition || (row && locations[row.base.id]);
            const title = source ? <button type="button" className="confirmation-entry-source" onClick={() => onLocate(groupId, source.sourceStart, source.sourceEnd)}>{itemTitle}</button> : <strong>{itemTitle}</strong>;
            return <tr key={row?.base.id || addition!.previewKey} className="confirmation-entry-detail" data-selected={(row ? selected.has(row.base.id) : selectedNew.has(addition!.previewKey)) || undefined} data-removed={row?.removed || undefined} data-preview-start={addition?.sourceStart} data-preview-end={addition?.sourceEnd} data-preview-group={addition ? groupId : undefined} data-preview-key={addition?.previewKey}>
        <td className="confirmation-entry-check"><input type="checkbox" aria-label={row ? `编辑选择 ${row.base.id}` : `选择新增 ${addition!.title} ${addition!.key}`} checked={row ? selected.has(row.base.id) : selectedNew.has(addition!.previewKey)} disabled={disabled || row?.removed} onChange={(event) => row ? selectExisting([row.base.id], event.target.checked) : onSelectNew(addition!.previewKey, event.target.checked)}/></td>

        <td className="confirmation-entry-item">{title}<small>{(line.unit === "场" ? "按场计费" : `${confirmationTravelerRoleLabel(line.travelerRole)} · 按人计费`)}</small>{showServiceSubject ? <small>服务主体：{line.fulfillmentSupplierName || "待设置"}</small> : null}</td>
        {<td className="confirmation-entry-formula-cell"><div className="confirmation-entry-formula">{compact(line.quantity)}{line.unit || "人"} × {compact(line.billingCount)}次 × {amountText(line.unitPriceAmount)}</div>{addition ? <button type="button" className="confirmation-entry-source-note" onClick={() => onLocate(groupId, addition.sourceStart, addition.sourceEnd)}>定位原文</button> : null}</td>}
        <td className="confirmation-entry-money">{amountText(amount)}</td>
        {<td className="confirmation-entry-subject-cell">{subject("fulfillment")}</td>}
        <td className="confirmation-entry-subject-cell">{subject("settlement")}</td>
        <td className="confirmation-entry-row-actions">{row ? row.removed ? "删除" : Object.keys(changedEntryFields(row)).length ? "修改" : "保留" : "新增"}{line.remark ? <details><summary>备注</summary><p>{line.remark}</p></details> : null}</td>
      </tr>;
        })}
    {issues.map((issue) => <tr key={`${issue.sourceStart}-${issue.message}`} className="confirmation-entry-issue" data-preview-start={issue.sourceStart} data-preview-end={issue.sourceEnd} data-preview-group={groupId}><td colSpan={columnCount}><button type="button" onClick={() => onLocate(groupId, issue.sourceStart, issue.sourceEnd, issue)}><strong>{issue.message}</strong><span>{issue.raw}</span></button></td></tr>)}
    {!records.length && !issues.length ? <tr><td colSpan={columnCount} className="confirmation-entry-empty">{active ? "在左侧填写费用，预览将在此展开" : "本组尚未录入费用"}</td></tr> : null}
    {rows.some((row) => row.removed) ? <tr><td colSpan={columnCount} className="confirmation-entry-warning">应用时仅移除本组明确标记的行。</td></tr> : null}
  </tbody>;
}
